import React from 'react'
import { Button, Icon, InputGroup } from '@blueprintjs/core';
import { observable, toJS } from 'mobx';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';

const selectAllId = "selectAll";

// Long lists (service types and the like) get a search box.
const SEARCH_AFTER = 8;

@observer
class ListPicker extends React.Component {

  @observable selectedOptions = [];
  @observable options = [];
  @observable query = '';

  syncFromProps() {
    if (!Array.isArray(this.props.selectedOptions)) {
      this.selectedOptions = Array.of(this.props.selectedOptions)
    } else {
      this.selectedOptions = this.props.selectedOptions;
    }
    this.options = this.props.options;
    let _component = this;
    this.options = this.options.map((option) => {
      if (_component.selectedOptions.includes(option.value)) {
        return { ...option, selected: true };
      }

      const object = _component.selectedOptions.find((selectedOption) => {
        return selectedOption && selectedOption.value === option.value;
      });
      if (object && object !== null) {
        // Keep the option's own display fields (swatch, pill); saved
        // selections often only carry label/value.
        return { ...option, ...object, swatch: option.swatch, pill: option.pill, selected: true };
      }

      // Allow callers (e.g. TechnicianPicker) to mark Select All based on
      // the currently visible/filtered options.
      if (option.value === selectAllId && option.selected) {
        return { ...option, selected: true };
      }

      return { ...option, selected: false };
    })
  }

  componentDidMount() {
    this.syncFromProps();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.options !== this.props.options ||
      prevProps.selectedOptions !== this.props.selectedOptions
    ) {
      this.syncFromProps();
    }
  }

  onClick(option, index) {
    const {
      props: {
        maximumSelections,
        onChange
      }
    } = this;
    this.options[index].selected = !this.options[index].selected;
    let allOptionItems = toJS(this.options);
    let selectedItems = allOptionItems.filter((e) => e.selected === true);
    if (!maximumSelections || (selectedItems.length <= maximumSelections)) {
      onChange(selectedItems.filter((e) => e.selected === true),this.options[index]);
    }else{
      let selectedSingleOption = [];
      allOptionItems.map(function(item,newIndex){
          if(newIndex === index){
            item.selected = true;
            selectedSingleOption.push(item);
          } else {
             item.selected = false;
             selectedSingleOption.push(item);
          } 
      });
      this.options = selectedSingleOption;
      onChange(selectedSingleOption.filter((e) => e.selected === true),this.options[index]);
    }
  }

  get isMultiSelect() {
    const { maximumSelections } = this.props;
    return !maximumSelections || maximumSelections > 1;
  }

  // Same rule as TechnicianPicker: multi-select panels stay open so several
  // options can be ticked in one go; single-select closes on pick.
  get dismissOnPick() {
    const { shouldDismissPopover } = this.props;
    if (shouldDismissPopover === false) return false;
    if (shouldDismissPopover === true) return true;
    return !this.isMultiSelect;
  }

  onClear() {
    this.options = this.options.map((option) => ({ ...option, selected: false }));
    this.props.onChange([], null);
  }

  onKeyDown(event, option, index) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onClick(option, index);
    }
  }

  renderOption(option, index) {
    const selected = !!option.selected;
    const isSelectAll = option.value === selectAllId;
    const className = [
      'option-picker__option',
      isSelectAll && 'option-picker__option--all',
      selected && 'is-selected',
      this.dismissOnPick && 'bp3-popover-dismiss',
    ]
      .filter(Boolean)
      .join(' ');
    return (
      <div
        key={`${option.value}-${index}`}
        role="option"
        aria-selected={selected}
        tabIndex={0}
        className={className}
        onClick={() => this.onClick(option, index)}
        onKeyDown={(event) => this.onKeyDown(event, option, index)}
      >
        <span className={`option-picker__check${selected ? ' is-checked' : ''}`}>
          {selected && <Icon icon="small-tick" iconSize={14} />}
        </span>
        {option.swatch && (
          <span className="option-picker__swatch" style={{ backgroundColor: option.swatch }} />
        )}
        {option.pill ? (
          <span
            className="option-picker__label option-picker__pill"
            style={{ backgroundColor: option.pill.background, color: option.pill.text }}
          >
            {option.label}
          </span>
        ) : (
          <span className="option-picker__label">{option.label}</span>
        )}
      </div>
    );
  }

  render() {
    const { options, query } = this;
    const indexed = options.map((option, index) => ({ option, index }));
    const selectAll = indexed.filter(({ option }) => option.value === selectAllId);
    const rest = indexed.filter(({ option }) => option.value !== selectAllId);
    const searchable = rest.length > SEARCH_AFTER;
    const needle = query.trim().toLowerCase();
    const visible = needle
      ? rest.filter(({ option }) => String(option.label).toLowerCase().includes(needle))
      : rest;
    const selectedCount = rest.filter(({ option }) => option.selected).length;

    return (
      <div className="option-picker">
        {searchable && (
          <div className="option-picker__search">
            <InputGroup
              leftIcon="search"
              placeholder="Search"
              value={query}
              autoFocus={true}
              onChange={(event) => { this.query = event.target.value; }}
            />
          </div>
        )}

        {!needle && selectAll.map(({ option, index }) => this.renderOption(option, index))}

        <div
          className="option-picker__list"
          role="listbox"
          aria-multiselectable={this.isMultiSelect}
        >
          {visible.map(({ option, index }) => this.renderOption(option, index))}
          {visible.length === 0 && (
            <div className="option-picker__empty">No matches for “{query.trim()}”</div>
          )}
        </div>

        {this.isMultiSelect && (
          <div className="option-picker__footer">
            <span>{selectedCount} selected</span>
            <Button
              minimal={true}
              small={true}
              disabled={selectedCount === 0}
              onClick={this.onClear.bind(this)}
            >
              Clear
            </Button>
          </div>
        )}
      </div>
    );
  }
}

ListPicker.propTypes = {
  onChange: PropTypes.func,
  options: PropTypes.array.isRequired,
  maximumSelections: PropTypes.number,
  shouldDismissPopover: PropTypes.bool,
  selectedOptions: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.string
  ]).isRequired
}

export default ListPicker;